import { AuthRoles } from '@hefe/auth';
import { t } from '@hefe/i18n';
import { currentUserSelector } from '@hefe/store';
import { clubHasSubscription, userHasRole } from '@hefe/utils';
import { Tooltip } from 'antd';
import React, { PropsWithChildren, ReactElement, ReactNode } from 'react';
import { useSelector } from 'react-redux';

interface SubscriptionWrapperProps {
  subscription: string[];
  title?: string;
}

const SubscriptionWrapper = ({
  subscription,
  title,
  children,
}: PropsWithChildren<SubscriptionWrapperProps>): ReactElement | undefined => {
  const user = useSelector(currentUserSelector);

  const shouldDisable = !clubHasSubscription(subscription, user);
  const userIsAdmin = userHasRole(AuthRoles.bbadmin, user);

  const recursivelyDisableChildren = (children: ReactNode): ReactNode => {
    return React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        const newProps: any = { disabled: shouldDisable };
        if (shouldDisable) {
          newProps['open'] = false;
        }

        const childElement = child as React.ReactElement<any>;
        if (childElement.props.children) {
          newProps.children = recursivelyDisableChildren(
            childElement.props.children
          );
        }

        return React.cloneElement(child, newProps);
      }

      return child;
    });
  };

  const disabledChildren = recursivelyDisableChildren(children);

  if (shouldDisable) {
    if (userIsAdmin) {
      const titleText = title ? `${title}: ` : '';
      return (
        <Tooltip
          arrow={false}
          title={titleText + t(`errors.permissions.${subscription[0]}`)}
        >
          {disabledChildren}
        </Tooltip>
      );
    } else {
      return undefined;
    }
  }

  return <div>{disabledChildren}</div>;
};

export default SubscriptionWrapper;
